/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';

// Lib
import { apiClient } from '@lib/api-client';
import {
	queryClient,
	TMutationConfig,
} from '@lib/react-query';

// I18n
import { useTranslation } from 'react-i18next';

// Constants
import { queryKeys } from '@config/constants/query-keys';
import i18n from '@config/i18n/config';
import { TApiResponseUpdatePassword } from '@features/auth/types';
import { TApiError } from '@global-types/api/api';
import { useLocalStorage } from '@hooks/useLocalStorage';
import useStore from '@store/index';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const updateProfile = (
	data: any
): Promise<TApiResponseUpdatePassword> => {
	const { id, ...rest } = data;
	return apiClient.patch(`/users/${id}`, {
		...rest,
	});
};

type TUseUpdateProfileOptions = {
	mutationConfig?: TMutationConfig<typeof updateProfile>;
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useUpdateProfile = ({
	mutationConfig,
}: TUseUpdateProfileOptions) => {
	const { t } = useTranslation();
	const [lng, setLng] = useLocalStorage('lng', 'fr');

	const { onSuccess } = mutationConfig || {};
	const { setUser: setUser } = useStore();
	return useMutation({
		mutationFn: updateProfile,
		onSuccess: (...args) => {
			queryClient.invalidateQueries({
				queryKey: [queryKeys.whoAmI.key],
			});
			i18n.changeLanguage(args[0].locale.language);
			setLng(args[0].locale.language);
			setUser(args[0]);
			onSuccess?.(...args);
			notifications.show({
				title: t('global.success'),
				message: t(
					'accountSettings.notifications.profilSuccessfullyUpdated'
				),
				color: 'var(--mantine-color-igluGreen-9)',
			});
		},
		onError: (error: TApiError) => {
			notifications.show({
				title: t('global.error'),
				message: error?.type
					? t(`api.${error.type}`)
					: t(`api.null`),
				color: 'var(--mantine-color-igluRed-9)',
			});
		},
	});
};
